import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { usePendingApprovals } from '../contexts/PendingApprovalsContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import { supabase } from '../lib/supabase';
import RemoveItemsDialog from './RemoveItemsDialog';
import ContactFormDialog from './ContactFormDialog';

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadCount } = useUnreadMessages();
  const { pendingCount, requiresConfirmation } = usePendingApprovals();
  const { items } = useWarehouse();
  
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);

  const mainMenuItems = [
    { path: '/dashboard', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />, label: 'Πίνακας Ελέγχου' },
    { path: '/clients', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />, label: 'Πελάτες' },
    { path: '/shifts', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Βάρδιες' },
    { path: '/employees', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />, label: 'Εργαζόμενοι' },
    { path: '/warehouse', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />, label: 'Αποθήκη' },
    { path: '/todo', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />, label: 'Εργασίες' },
    { path: '/messages', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />, label: 'Μηνύματα', hasUnread: unreadCount > 0 },
    ...(requiresConfirmation ? [{ path: '/pending-approvals', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />, label: 'Εγκρίσεις', hasUnread: pendingCount > 0 }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error signing out:', error);
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-[#1F2937] bg-[#111827] sticky top-0 z-40">
        <h1 className="text-xl font-bold text-white flex items-center gap-1">
          <span className="text-[#10B981]">Tail</span>Clinic
        </h1>
        <button 
          onClick={() => setIsSidebarOpen(true)} 
          className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
        </button>
      </div>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Sidebar (Τώρα χρησιμοποιεί lg:sticky & flex-shrink-0 για να μην επικαλύπτει) */}
      <aside 
        className={`
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 
          w-[280px] lg:w-64 h-screen bg-[#111827] border-r border-[#1F2937] 
          flex flex-col flex-shrink-0 transition-transform duration-300 ease-in-out shadow-2xl lg:shadow-none
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 lg:p-6 border-b border-[#1F2937] flex-shrink-0">
          <h1 className="text-2xl font-bold text-white flex items-center gap-1">
            <span className="text-[#10B981]">Tail</span>Clinic
          </h1>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        {/* ΜΑΓΕΙΑ ΓΙΑ ΚΙΝΗΤΑ: Όλα τα υπόλοιπα μπαίνουν σε ΕΝΑ scrollable container */}
        <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
          
          {/* CTAs */}
          <div className="p-4 border-b border-[#1F2937] flex flex-col gap-3 flex-shrink-0">
            <button
              onClick={() => { navigate('/new-appointment'); setIsSidebarOpen(false); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#10B981] text-[#111827] rounded-lg font-bold text-sm shadow-[0_4px_12px_rgba(16,185,129,0.2)] transition-transform hover:scale-[1.02]"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
              Νέο Ραντεβού
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { navigate('/new-client'); setIsSidebarOpen(false); }} className="w-full py-2 px-1 text-xs font-semibold text-gray-300 border border-gray-700 rounded-lg hover:border-blue-500 hover:text-blue-500 transition-colors">
                + Πελάτης
              </button>
              <button onClick={() => { setShowRemoveDialog(true); setIsSidebarOpen(false); }} disabled={!items || items.length === 0} className={`w-full py-2 px-1 text-xs font-semibold rounded-lg border transition-colors ${items && items.length > 0 ? 'text-gray-300 border-gray-700 hover:border-yellow-500 hover:text-yellow-500' : 'text-gray-600 border-gray-800 cursor-not-allowed'}`}>
                - Προϊόν
              </button>
            </div>
          </div>

          {/* Main Nav */}
          <nav className="p-3 space-y-1 flex-shrink-0">
            {mainMenuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setIsSidebarOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.path) ? 'bg-[#10B981]/10 text-[#10B981]' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
              >
                <div className="flex items-center gap-3">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">{item.icon}</svg>
                  {item.label}
                </div>
                {item.hasUnread && (
                  <span className="relative flex h-2.5 w-2.5 mr-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 shadow-sm"></span>
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Κενό για να σπρώχνει το Footer κάτω σε μεγάλες οθόνες */}
          <div className="flex-1"></div>

          {/* Footer (Ρυθμίσεις / Προφίλ) */}
          <div className="p-4 border-t border-[#1F2937] mt-auto flex-shrink-0">
            <div className="flex flex-col gap-1 mb-4">
              <button onClick={() => { navigate('/subscription'); setIsSidebarOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
                Συνδρομή
              </button>
              <button onClick={() => { navigate('/settings'); setIsSidebarOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                Ρυθμίσεις
              </button>
              <button onClick={() => { setShowContactDialog(true); setIsSidebarOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                Επικοινωνία
              </button>
            </div>

            <div className="flex items-center justify-between p-2 rounded-xl bg-gray-800/40 border border-gray-700/50 hover:bg-gray-800/70 transition-colors group">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#10B981] flex items-center justify-center text-[#111827] font-bold text-xs shadow-sm">
                  TC
                </div>
                <div className="flex flex-col truncate">
                  <span className="text-sm font-semibold text-white truncate">Account</span>
                  <span className="text-xs text-[#10B981] truncate">Κλινική</span>
                </div>
              </div>
              
              <button onClick={handleSignOut} title="Αποσύνδεση" className="p-2 text-gray-400 group-hover:text-red-400 hover:!bg-red-500/10 hover:!text-red-500 rounded-lg transition-all flex-shrink-0">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Dialogs */}
      <RemoveItemsDialog isOpen={showRemoveDialog} onClose={() => setShowRemoveDialog(false)} />
      <ContactFormDialog isOpen={showContactDialog} onClose={() => setShowContactDialog(false)} />
    </>
  );
};

export default Sidebar;